import { VALIDATION_MESSAGES } from "./journalEntryConstants";

export interface ValidationErrors {
  [key: string]: string;
}

export interface LineValidationErrors {
  [lineId: string]: {
    [field: string]: string;
  };
}

export interface FormData {
  entry_date: Date | null;
  narration: string;
  reference_id: string;
  branch_id: string;
  currency: string;
  journal_type: string;
  journal_lines: JournalLineForm[];
}

export interface JournalLineForm {
  id: string;
  account_id: string;
  account_code: string;
  account_name: string;
  debit: number | null;
  credit: number | null;
  description: string;
  branch_override?: string;
  cost_center?: string;
}

export interface JournalTotals {
  totalDebit: number;
  totalCredit: number;
  difference: number;
  isBalanced: boolean;
}

export interface FormValidationState {
  hasRequiredFields: boolean;
  hasValidLines: boolean;
  isBalanced: boolean;
  canSaveAsDraft: boolean;
  canPost: boolean;
}

/**
 * Validates journal entry header fields
 */
export const validateHeader = (formData: FormData): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!formData.entry_date) {
    errors.entry_date = VALIDATION_MESSAGES.ENTRY_DATE_REQUIRED;
  }

  if (!formData.narration.trim()) {
    errors.narration = VALIDATION_MESSAGES.NARRATION_REQUIRED;
  }

  if (!formData.branch_id) {
    errors.branch_id = VALIDATION_MESSAGES.BRANCH_REQUIRED;
  }

  return errors;
};

/**
 * Validates individual journal line
 */
export const validateJournalLine = (
  line: JournalLineForm
): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!line.account_id) {
    errors.account = VALIDATION_MESSAGES.ACCOUNT_REQUIRED;
  }

  if (line.debit === null && line.credit === null) {
    errors.amount = VALIDATION_MESSAGES.AMOUNT_REQUIRED;
  }

  if (
    line.debit !== null &&
    line.credit !== null &&
    line.debit > 0 &&
    line.credit > 0
  ) {
    errors.amount = VALIDATION_MESSAGES.BOTH_AMOUNTS_ERROR;
  }

  if (line.debit !== null && line.debit < 0) {
    errors.debit = VALIDATION_MESSAGES.NEGATIVE_DEBIT;
  }

  if (line.credit !== null && line.credit < 0) {
    errors.credit = VALIDATION_MESSAGES.NEGATIVE_CREDIT;
  }

  return errors;
};

/**
 * Validates all journal lines
 */
export const validateJournalLines = (
  lines: JournalLineForm[]
): LineValidationErrors => {
  const lineErrors: LineValidationErrors = {};

  for (const line of lines) {
    const errors = validateJournalLine(line);
    if (Object.keys(errors).length > 0) {
      lineErrors[line.id] = errors;
    }
  }

  return lineErrors;
};

/**
 * Validates journal balance
 */
export const validateBalance = (
  totals: JournalTotals,
  currency: string
): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!totals.isBalanced) {
    errors.balance = `${
      VALIDATION_MESSAGES.UNBALANCED_ENTRY
    } ${currency} ${Math.abs(totals.difference).toFixed(2)}`;
  }

  return errors;
};

/**
 * Checks if form has minimum required fields for draft saving
 */
export const hasMinimumRequiredFields = (formData: FormData): boolean => {
  return !!(
    formData.entry_date &&
    formData.narration.trim() &&
    formData.branch_id
  );
};

/**
 * Checks if journal lines have valid data (at least one complete line)
 */
export const hasValidJournalLines = (lines: JournalLineForm[]): boolean => {
  return lines.some(
    (line) =>
      line.account_id &&
      (line.debit !== null || line.credit !== null) &&
      (line.debit === null || line.debit >= 0) &&
      (line.credit === null || line.credit >= 0)
  );
};

/**
 * Professional form validation state calculator
 * Determines what actions are available based on current form state
 */
export const getFormValidationState = (
  formData: FormData,
  totals: JournalTotals
): FormValidationState => {
  const hasRequiredFields = hasMinimumRequiredFields(formData);
  const hasValidLines = hasValidJournalLines(formData.journal_lines);
  const isBalanced = totals.isBalanced;

  // Draft can be saved with minimum fields and at least one valid line
  const canSaveAsDraft = hasRequiredFields && hasValidLines;

  // Posting requires everything to be perfect: required fields, valid lines, and balanced
  const canPost = hasRequiredFields && hasValidLines && isBalanced;

  return {
    hasRequiredFields,
    hasValidLines,
    isBalanced,
    canSaveAsDraft,
    canPost,
  };
};

/**
 * Comprehensive form validation
 */
export const validateJournalEntryForm = (
  formData: FormData,
  totals: JournalTotals
): {
  headerErrors: ValidationErrors;
  lineErrors: LineValidationErrors;
  balanceErrors: ValidationErrors;
  isValid: boolean;
} => {
  const headerErrors = validateHeader(formData);
  const lineErrors = validateJournalLines(formData.journal_lines);
  const balanceErrors = validateBalance(totals, formData.currency);

  const isValid =
    Object.keys(headerErrors).length === 0 &&
    Object.keys(lineErrors).length === 0 &&
    Object.keys(balanceErrors).length === 0;

  return {
    headerErrors,
    lineErrors,
    balanceErrors,
    isValid,
  };
};
